import React, { createContext, useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const accessTokenRef = useRef(null);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = (error, token) => {
    failedQueue.current.forEach(p => error ? p.reject(error) : p.resolve(token));
    failedQueue.current = [];
  };

  // Token persistence helpers — sessionStorage as fallback when cookies fail cross-domain
  const persistToken = (token) => {
    accessTokenRef.current = token;
    if (token) {
      try { sessionStorage.setItem('ci_at', token); } catch {}
    } else {
      try { sessionStorage.removeItem('ci_at'); sessionStorage.removeItem('ci_rt'); } catch {}
    }
  };

  const persistRefreshToken = (token) => {
    if (token) {
      try { sessionStorage.setItem('ci_rt', token); } catch {}
    }
  };

  const getStoredToken = () => {
    if (accessTokenRef.current) return accessTokenRef.current;
    try { return sessionStorage.getItem('ci_at'); } catch { return null; }
  };

  const getStoredRefreshToken = () => {
    try { return sessionStorage.getItem('ci_rt'); } catch { return null; }
  };

  // Create a stable axios instance
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    // Request interceptor: attach access token
    instance.interceptors.request.use((config) => {
      const token = getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor: handle 401 with refresh
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            });
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          try {
            // Try cookie-based refresh first, with sessionStorage token as fallback body
            const storedRt = getStoredRefreshToken();
            const res = await axios.post(
              `${API}/auth/refresh`,
              storedRt ? { refresh_token: storedRt } : {},
              { withCredentials: true }
            );
            const { access_token, refresh_token } = res.data;
            persistToken(access_token);
            if (refresh_token) persistRefreshToken(refresh_token);

            isRefreshing.current = false;
            processQueue(null, access_token);

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return instance(originalRequest);
          } catch (refreshError) {
            isRefreshing.current = false;
            processQueue(refreshError, null);
            persistToken(null);
            setUser(null);
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: attempt silent refresh
  const fetchUser = useCallback(async () => {
    try {
      const storedRt = getStoredRefreshToken();
      const refreshRes = await axios.post(
        `${API}/auth/refresh`,
        storedRt ? { refresh_token: storedRt } : {},
        { withCredentials: true }
      );
      const { access_token, refresh_token, user: userData } = refreshRes.data;
      persistToken(access_token);
      if (refresh_token) persistRefreshToken(refresh_token);
      setUser(userData);
    } catch {
      persistToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    persistToken(access_token);
    if (refresh_token) persistRefreshToken(refresh_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { access_token, refresh_token, user: newUser } = response.data;
    persistToken(access_token);
    if (refresh_token) persistRefreshToken(refresh_token);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      persistToken(null);
      setUser(null);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const changePassword = async (current_password, new_password) => {
    const res = await api.post('/auth/change-password', { current_password, new_password });
    persistToken(res.data.access_token);
    if (res.data.refresh_token) persistRefreshToken(res.data.refresh_token);
    return res.data;
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    isAuthenticated,
    api,
    accessToken: accessTokenRef.current,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
